import { BaseConfigurator, ARROW_SVG } from './base-configurator.js';
import { CartAddEvent } from '@theme/events';

class ProductConfigurator extends BaseConfigurator {
  requiredRefs = [
    'drawer', 'backdrop', 'closeBtn', 'stepNav', 'stepContent',
    'summary', 'totalPrice', 'addToCart', 'configData',
    'qtyWrap', 'qtyMinus', 'qtyPlus', 'qtyInput',
  ];

  #pricing = null;
  #variantId;
  #basePrice;
  #variantPrices = {};
  #variantImages = {};

  parseConfig() {
    try {
      const raw = JSON.parse(this.refs.configData.textContent ?? '{}');
      this.#pricing = raw.pricing ?? null;
      this.#variantId = raw.variantId;
      this.#basePrice = raw.basePrice;
      this._currencyCode = raw.currencyCode ?? 'EUR';
      this.#variantPrices = raw.variantPrices ?? {};
      this.#variantImages = raw.variantImages ?? {};
    } catch (e) {
      console.error('[Configurator] Parse config failed:', e);
    }
  }

  setupFooterEvents() {
    const { addToCart, qtyMinus, qtyPlus, qtyInput } = this.refs;
    addToCart.addEventListener('click', (e) => this.#handleFooterClick(e));
    qtyMinus.addEventListener('click', () => this.#adjustQty(-1));
    qtyPlus.addEventListener('click', () => this.#adjustQty(1));
    qtyInput.addEventListener('change', () => this.#clampQty());
  }

  bindContentEvents(content) {
    content.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('[data-step-key][data-value]');
      if (!btn || btn.getAttribute('role') !== 'radio') return;
      e.preventDefault();
      this.#handleOptionClick(btn);
    });
  }

  onInit() {
    this.#readStepsFromDom();
    this.#readSelectionsFromDom();
    this.#formatCardPrices();
  }

  updateSummary() {
    const summary = this.refs.summary;
    if (!summary) return;
    summary.innerHTML = '';
    const list = document.createElement('dl');
    list.className = 'configurator__summary-list';

    for (const step of this._steps) {
      const value = this._selections[step.key];
      if (value === undefined || value === false) continue;
      const subValue = this._selections[step.key + '_sub'];
      const dt = document.createElement('dt');
      dt.className = 'configurator__summary-label';
      dt.textContent = step.label;
      const dd = document.createElement('dd');
      dd.className = 'configurator__summary-value';

      if (subValue) {
        const subLabel = this._getOptionLabel(step.key, subValue) || String(subValue);
        dd.textContent = `${String(value)} — ${subLabel}`;
      } else {
        dd.textContent = this._getOptionLabel(step.key, value) || String(value);
      }
      list.append(dt, dd);
    }
    summary.append(list);
  }

  updatePrice() {
    const price = this.#calculatePrice();
    if (this.refs.totalPrice) this.refs.totalPrice.textContent = this.#formatPrice(price);
  }

  updateFooterButton() {
    const btn = this.refs.addToCart;
    const label = this.refs.addToCartText;
    const qtyWrap = this.refs.qtyWrap;
    if (!btn || !label) return;

    const isLastStep = this._currentStepIndex >= this._steps.length - 1;
    if (qtyWrap) qtyWrap.hidden = !isLastStep;

    if (isLastStep) {
      label.textContent = 'In den Warenkorb';
      btn.disabled = !this.#isValid();
      if (this.#isValid() && this.refs.validationMessage) this.refs.validationMessage.hidden = true;
    } else {
      const next = this._steps[this._currentStepIndex + 1];
      label.innerHTML = next ? `Weiter zu ${next.label} ${ARROW_SVG}` : `Weiter ${ARROW_SVG}`;
      btn.disabled = false;
    }
  }

  #handleFooterClick(e) {
    const isLastStep = this._currentStepIndex >= this._steps.length - 1;
    if (isLastStep) {
      this.#handleAddToCart(e);
    } else {
      e.preventDefault();
      this._showStep(this._currentStepIndex + 1);
    }
  }

  #handleOptionClick(button) {
    const key = button.getAttribute('data-step-key');
    const value = button.getAttribute('data-value');
    const variantKey = button.getAttribute('data-variant-key');
    const groupValue = button.getAttribute('data-group-value');
    if (!key || !value) return;

    this._selections[key] = value;
    if (variantKey && groupValue != null) {
      this._selections[variantKey] = groupValue;
      this._selections[key + '_sub'] = value;
    }

    const stepPanel = button.closest('.configurator__step');
    if (stepPanel) {
      stepPanel.querySelectorAll('[role="radio"]').forEach((r) => {
        const isChecked = r === button;
        r.setAttribute('aria-checked', isChecked ? 'true' : 'false');
        r.classList.toggle('configurator__option-card--active', isChecked);
        r.classList.toggle('configurator__option-button--active', isChecked);
      });
    }

    this._afterSelectionChange();
    this.#refreshCardPrices();
  }

  async #handleAddToCart(e) {
    e.preventDefault();
    if (!this.#isValid()) {
      const el = this.refs.validationMessage;
      if (el) {
        el.textContent = 'Bitte füllen Sie alle Pflichtfelder aus.';
        el.hidden = false;
      }
      return;
    }

    const addButton = this.refs.addToCart;
    const buttonText = this.refs.addToCartText;
    const spinner = this.refs.addToCartSpinner;
    addButton.disabled = true;
    buttonText.textContent = 'Wird hinzugefügt…';
    if (spinner) spinner.hidden = false;

    try {
      const variantId = this.#resolveVariantId();
      const properties = this.#buildCartProperties();
      const quantity = Math.max(1, parseInt(this.refs.qtyInput?.value, 10) || 1);

      // Collect cart-drawer section IDs so the response includes pre-rendered
      // section HTML that cart-items-component can morph synchronously.
      const sectionIds = [];
      for (const el of document.querySelectorAll('cart-items-component')) {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);

      const payload = { id: variantId, quantity, properties };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      const addData = await response.json();

      // Fetch the real cart total while showing the success message.
      // Race against a 3 s cap so a hung /cart.js never blocks the UI.
      const cartPromise = Promise.race([
        fetch('/cart.js').then((r) => r.json()).catch(() => null),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);

      buttonText.textContent = 'Hinzugefügt ✓';

      // Wait for both the success display and the (bounded) cart fetch
      const [cart] = await Promise.all([
        cartPromise,
        new Promise((resolve) => setTimeout(resolve, 1200)),
      ]);

      // Close the configurator completely before opening the cart drawer
      // to avoid overlapping drawers fighting over body scroll state
      this.closeDrawer();
      buttonText.textContent = 'In den Warenkorb';
      addButton.disabled = false;
      if (spinner) spinner.hidden = true;

      // Allow the close transition to finish (base class uses 400ms fallback)
      await new Promise((resolve) => setTimeout(resolve, 450));

      this.dispatchEvent(
        new CartAddEvent(cart || {}, String(variantId), {
          productId: this.dataset.productId,
          ...(cart && { itemCount: cart.item_count }),
          ...(addData.sections && { sections: addData.sections }),
        })
      );
    } catch (err) {
      console.error('[Configurator] Add to cart error:', err);
      buttonText.textContent = err.name === 'AbortError' ? 'Zeitüberschreitung – erneut versuchen' : 'Fehler – erneut versuchen';
      addButton.disabled = false;
      if (spinner) spinner.hidden = true;
    }
  }

  #readStepsFromDom() {
    const content = this._getStepContent();
    const panels = content?.querySelectorAll('.configurator__step');
    if (!panels?.length) return;
    this._steps = [...panels].map((el) => ({
      key: el.getAttribute('data-step-key') ?? '',
      label: el.getAttribute('data-step-label') ?? '',
      variant_key: el.getAttribute('data-variant-key') || undefined,
    }));
  }

  #readSelectionsFromDom() {
    this._selections = {};
    const content = this._getStepContent();
    if (!content) return;

    for (const panel of content.querySelectorAll('.configurator__step')) {
      const key = panel.getAttribute('data-step-key');
      const variantKey = panel.getAttribute('data-variant-key');
      const checked = panel.querySelector('[role="radio"][aria-checked="true"]');
      if (!checked) continue;

      const value = checked.getAttribute('data-value');
      if (key && value) this._selections[key] = value;
      if (variantKey) {
        const groupValue = checked.getAttribute('data-group-value');
        if (groupValue) {
          this._selections[variantKey] = groupValue;
          this._selections[key + '_sub'] = value;
        }
      }
    }
  }

  #calculatePrice() {
    if (!this.#pricing || this.#pricing.type !== 'variants') return this.#basePrice;
    const variantId = this.#resolveVariantId();
    return this.#variantPrices[variantId] ?? this.#variantPrices[String(variantId)] ?? this.#basePrice;
  }

  #resolveVariantId() {
    if (!this.#pricing?.variant_key_fields?.length || !this.#pricing.variant_map) return this.#variantId;
    const key = this.#pricing.variant_key_fields
      .map((field) => {
        let val = this._selections[field];
        if (val === undefined || val === '') val = this.#getFirstValueForField(field);
        if (val === true) return 'true';
        if (val === false) return 'false';
        return String(val ?? '');
      })
      .join('_');
    const id = this.#pricing.variant_map[key];
    return id !== undefined && id !== null ? Number(id) : this.#variantId;
  }

  #getFirstValueForField(field) {
    const content = this._getStepContent();
    if (!content) return '';
    const panel = content.querySelector(`.configurator__step[data-step-key="${field}"], .configurator__step[data-variant-key="${field}"]`);
    const first = panel?.querySelector('[data-value], [data-group-value]');
    if (!first) return '';
    const group = first.getAttribute('data-group-value');
    return group ?? first.getAttribute('data-value') ?? '';
  }

  #isValid() {
    for (const step of this._steps) {
      const value = this._selections[step.key];
      if (value === undefined || value === '' || value === false) return false;
    }
    return true;
  }

  #buildCartProperties() {
    const properties = {};
    for (const step of this._steps) {
      const value = this._selections[step.key];
      if (value === undefined || value === false) continue;
      const subValue = this._selections[step.key + '_sub'];
      if (subValue) {
        properties[step.label] = this._getOptionLabel(step.key, subValue) || String(subValue);
      } else {
        properties['_' + step.label] = this._getOptionLabel(step.key, value) || String(value);
      }
    }
    properties['_cfgSelections'] = JSON.stringify(this._selections);
    return properties;
  }

  #formatCardPrices() {
    const content = this._getStepContent();
    if (!content) return;
    for (const card of content.querySelectorAll('[data-price]')) {
      const priceEl = card.querySelector('.configurator__option-card-price');
      if (!priceEl) continue;
      const cents = parseInt(card.getAttribute('data-price'), 10);
      if (!isNaN(cents)) priceEl.textContent = this.#formatPrice(cents);
    }
  }

  #refreshCardPrices() {
    if (!this.#pricing?.variant_key_fields?.length || !this.#pricing.variant_map) return;
    const content = this._getStepContent();
    if (!content) return;

    for (const panel of content.querySelectorAll('.configurator__step')) {
      const stepKey = panel.getAttribute('data-step-key');
      const variantKey = panel.getAttribute('data-variant-key');

      for (const card of panel.querySelectorAll('[data-price][role="radio"]')) {
        const cardGroupValue = card.getAttribute('data-group-value');
        const cardValue = card.getAttribute('data-value');

        const tempSelections = { ...this._selections };
        if (variantKey && cardGroupValue) {
          tempSelections[variantKey] = cardGroupValue;
        } else if (stepKey) {
          tempSelections[stepKey] = cardValue;
        }

        const key = this.#pricing.variant_key_fields
          .map((field) => {
            let val = tempSelections[field];
            if (val === undefined || val === '') val = this.#getFirstValueForField(field);
            return String(val ?? '');
          })
          .join('_');

        const vid = this.#pricing.variant_map[key];
        if (vid == null) continue;
        const price = this.#variantPrices[vid] ?? this.#variantPrices[String(vid)];
        if (price == null) continue;

        card.setAttribute('data-price', String(price));
        const priceEl = card.querySelector('.configurator__option-card-price');
        if (priceEl) priceEl.textContent = this.#formatPrice(price);
      }
    }
  }

  #adjustQty(delta) {
    const input = this.refs.qtyInput;
    if (!input) return;
    const val = Math.min(99, Math.max(1, parseInt(input.value, 10) + delta));
    input.value = isNaN(val) ? 1 : val;
  }

  #clampQty() {
    const input = this.refs.qtyInput;
    if (!input) return;
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (val > 99) val = 99;
    input.value = val;
  }

  #formatPrice(priceInCents) {
    return this._priceFormatter.format(priceInCents / 100);
  }
}

customElements.define('custom-product-configurator', ProductConfigurator);
